import React, {useState, useEffect, useCallback} from 'react';
import {Col, Form, Spinner, Row, Stack} from 'react-bootstrap';
import {useAppContext} from 'contexts/ApplicationContext';
import {Dropdown, DropdownOption} from 'shared/components/Forms/Dropdown';
import styled from 'styled-components';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {JobForm} from 'components/customer/Job/entity/JobForm';
import {useFormikContext} from 'formik';

interface State {
    id: number | null;
    name: string;
}

export const DefaultDeliveryAddress = ({
    displayOnly = true,
    loading = false,
    isNameVisible = true,
}: {
    displayOnly?: boolean;
    loading?: boolean;
    isNameVisible?: boolean;
}) => {
    const {userProfile, places, stateLabel} = useAppContext();
    const isSmallDevice = isDeviceSmall();
    const {
        values,
        setFieldValue: setFieldValueOG,
        handleChange,
    } = useFormikContext<JobForm>();

    const [states, setStates] = useState<State[]>([]);
    const [currentState, setCurrentState] = useState<State>({
        id: null,
        name: '',
    });

    const handleSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
        },
        []
    );

    const setFieldValue = useCallback(
        (field: string, value?: string | number | boolean) => {
            if (typeof value !== 'boolean') {
                void setFieldValueOG(field, value);
            }
        },
        []
    );

    useEffect(() => {
        if (
            places &&
            places.Countries &&
            userProfile &&
            userProfile.countryName
        ) {
            setStates(places.Countries[userProfile.countryName]);
        }
    }, [userProfile, places]);

    useEffect(() => {
        if (states.length > 0) {
            const currentState = states.find(
                (state) => state.id == values.state
            );

            if (currentState) {
                setCurrentState(currentState);
            }
        }
    }, [states, values.state]);

    return (
        <div>
            {!displayOnly ? (
                <Row>
                    <Col md={12} sm={12}>
                        <StyledForm
                            className="cbc-form reduced-pb"
                            style={{paddingLeft: '0px'}}
                            $isMobile={isSmallDevice}
                            onSubmit={handleSubmit}>
                            {isNameVisible ? (
                                <FormGroup asRow controlId="name_">
                                    <Form.Label
                                        column
                                        md={{offset: 0, span: 2}}>
                                        Name:{' '}
                                    </Form.Label>
                                    <Col md="10">
                                        <StyleFormControl
                                            type="text"
                                            name="name"
                                            value={values.name ?? ''}
                                            onChange={handleChange}
                                            placeholder="Optional"
                                        />
                                    </Col>
                                </FormGroup>
                            ) : null}
                            <FormGroup asRow controlId="address_">
                                <Form.Label column md={{offset: 0, span: 2}}>
                                    Address:{' '}
                                </Form.Label>
                                <Col md="10">
                                    <Form.Control
                                        type="text"
                                        name="street"
                                        value={values.street ?? ''}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </FormGroup>
                            {userProfile?.countryName != 'Australia' ? (
                                <FormGroup asRow controlId="city_">
                                    <Form.Label
                                        column
                                        md={{offset: 0, span: 2}}>
                                        City:{' '}
                                    </Form.Label>
                                    <Col md="10">
                                        <Form.Control
                                            type="text"
                                            name="city"
                                            value={values.city ?? ''}
                                            onChange={handleChange}
                                        />
                                    </Col>
                                </FormGroup>
                            ) : null}
                            <FormGroup asRow controlId="suburb_">
                                <Form.Label column md={{offset: 0, span: 2}}>
                                    Suburb:{' '}
                                </Form.Label>
                                <Col md="10">
                                    <Form.Control
                                        type="text"
                                        name="suburb"
                                        value={values.suburb ?? ''}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </FormGroup>
                            <FormGroup asRow controlId="postcode_">
                                <Form.Label column md={{offset: 0, span: 2}}>
                                    Postcode:{' '}
                                </Form.Label>
                                <Col md={{offset: 0, span: 4}} sm={5} xs={7}>
                                    <Form.Control
                                        type="number"
                                        name="postcode"
                                        value={values.postcode ?? ''}
                                        onChange={handleChange}
                                    />
                                </Col>
                                <Form.Label
                                    column
                                    md={{offset: 0, span: 2}}
                                    sm={8}
                                    xs={6}
                                    style={
                                        !isSmallDevice
                                            ? {textAlign: 'right'}
                                            : {}
                                    }>
                                    {stateLabel ? stateLabel : 'State'}:
                                </Form.Label>
                                <Col md={{offset: 0, span: 4}} sm={5} xs={7}>
                                    <Dropdown
                                        options={
                                            states as unknown as DropdownOption[]
                                        }
                                        name="state"
                                        value={values.state}
                                        selectHandler={setFieldValue}
                                    />
                                </Col>
                            </FormGroup>
                        </StyledForm>
                    </Col>
                </Row>
            ) : (
                <Row>
                    <Col md={12} sm={12}>
                        <StyledForm
                            className="cbc-form reduced-pb"
                            style={{
                                paddingLeft: '0px',
                            }}
                            $isMobile={isSmallDevice}
                            onSubmit={handleSubmit}>
                            {isNameVisible ? (
                                <FormGroup asRow controlId="name_">
                                    <Form.Label
                                        column
                                        md={{offset: 0, span: 2}}>
                                        Name:{' '}
                                    </Form.Label>
                                    <Col md="10">
                                        <StyleFormControl
                                            type="text"
                                            name="name"
                                            value={values.name}
                                            onChange={handleChange}
                                            placeholder="Optional"
                                        />
                                    </Col>
                                </FormGroup>
                            ) : null}
                            <Stack direction="horizontal" gap={3}>
                                <Label style={{maxWidth: '100%'}}>
                                    Address:{' '}
                                </Label>
                                <div style={{alignSelf: 'center'}}>
                                    {values.street ? `${values.street},` : ''}{' '}
                                    {values.city ? `${values.city},` : ''}{' '}
                                    {values.suburb ? `${values.suburb},` : ''}{' '}
                                    {currentState
                                        ? `${currentState.name},`
                                        : ''}{' '}
                                    {values.postcode ? values.postcode : ''}
                                    {loading ? (
                                        <StyledSpinner
                                            animation="border"
                                            size="sm"
                                        />
                                    ) : null}
                                </div>
                            </Stack>
                        </StyledForm>
                    </Col>
                </Row>
            )}
        </div>
    );
};

const Label = styled.label`
    font-weight: bold;
    color: ${({theme}) => theme.colors.primary?.main};
`;

const StyledSpinner = styled(Spinner)`
    margin-left: 10px;
`;

const StyleFormControl = styled(Form.Control)`
    background: #fff;
    ::placeholder {
        font-style: italic;
    }
`;

const StyledForm = styled(Form)<{$isMobile?: boolean}>`
    ${({$isMobile}) =>
        $isMobile
            ? ` margin-top: 5px;
                .form-group {
                    margin-bottom: 0;
                }`
            : ''}
`;
